import {
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { Reorder, useDragControls, useInstantLayoutTransition, type Transition } from 'framer-motion'
import { Calendar, ListChecks, Clock, UserStar } from 'lucide-react'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { Collapsible } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'

import { SidepanelSection } from './nested/section'
import { SidepanelSectionHeader, SidepanelSectionHeaderLead } from './nested/section-header'
import { SidepanelSectionTitleGroup } from './nested/section-title-group'
import { SidepanelSectionIcon } from './nested/section-icon'
import { SidepanelSectionLabel } from './nested/section-label'
import { SidepanelSectionSubtitle } from './nested/section-subtitle'
import { SidepanelSectionViewAll } from './nested/section-view-all'
import { SidepanelSectionDragHandle } from './nested/section-drag-handle'
import { useDragAutoScroll } from './use-drag-auto-scroll'
import { usePostDragClickGuard } from './use-post-drag-click-guard'
import { useHasHistoryActivity } from '../../hooks/use-history-activity'
import { TodosHome } from './todos-home'

import { SidepanelUpcomingSection, UpcomingLoadingIndicator } from './sections/upcoming'
import { SidepanelUpcomingList } from './sections/upcoming-list'
import { SidepanelRemindersSection, RemindersBadge } from './sections/reminders'
import { SidepanelHistorySection } from './sections/history'
import { ForYouUnreadBadge, SidepanelRunningChatsSection } from './sections/running-chats'

import {
  useSidepanelSectionOrder,
  useSetSidepanelSectionOrder,
  useIsSidepanelSectionCollapsed,
  useSetSidepanelSectionCollapsed,
} from '../../atoms'
import { useViewActions, type OpenViewFunction } from '../../view-atoms'
import { sanitizeSidepanelSectionOrder, type SidepanelSectionKey } from '../../sidepanel-section-order'
import { isDragExcluded } from '../../drag-exclusion'
import type { QuickTourTarget } from '../quicktour/build-tour-steps'
import { useSidepanelSurface } from '../../profile/use-sidepanel-profile'

// Reorder swap animation. `layout` stays on so framer's projection tracks
// item positions for correct drag/reorder; the one layout change we DON'T want
// animated — the sidepanel width commit — is applied via an instant layout
// transition at the resize callsite instead of being gated here.
const reorderTransition: Transition = { type: 'spring', stiffness: 600, damping: 38, mass: 0.7 }

const sameOrder = (a: Array<SidepanelSectionKey>, b: Array<SidepanelSectionKey>) =>
  a.length === b.length && a.every((key, i) => key === b[i])

// On release a Reorder item springs its drag offset back to its slot (snap to
// origin), so the settle is governed by bounceStiffness/bounceDamping — NOT the
// free-inertia power/timeConstant. A stiff, well-damped spring keeps the
// momentum feel but lands the dropped section as snappily as the siblings.
const dropDragTransition = { bounceStiffness: 900, bounceDamping: 48 }

interface SectionDescriptor {
  icon: ReactNode
  // Nodes let a section derive its label from live state.
  label: ReactNode
  // Static name for accessible labels on the header's controls, which can't use
  // `label` when it's a live node.
  name: string
  body: ReactNode
  // Rendered inside the title group, after the label (e.g. unread counts).
  badge?: ReactNode
  // Rendered in the header's trailing slot (a secondary line, e.g. a loading
  // indicator). Yields to "View all" while the header is hovered.
  subtitle?: ReactNode
  // Where header navigation lands. Spelled out per section rather than reusing the
  // section key as a view id: the two matching today is a coincidence, and
  // relying on it means a new section without a matching View fails at its
  // header instead of here. Declared as a call rather than a context object so
  // the id stays a literal and `openView`'s per-view context type still applies
  // — which is also what lets a section whose Home preview is a slice of one
  // particular tab open on that tab.
  viewAll?: (openView: OpenViewFunction) => void
  headerAction?: ReactNode
}

/** Switches the `upcoming` surface: a lead card then rows with hover buttons, or flat rows that open a popover. */
const UpcomingSectionBody = memo(function UpcomingSectionBody() {
  return useSidepanelSurface('upcoming') === 'rows' ? <SidepanelUpcomingList /> : <SidepanelUpcomingSection />
})

interface SectionHeaderContent {
  lead: ReactNode
  trailing: ReactNode
  onViewAll?: () => void
}

// Single source of truth for each sidepanel section: its header bits and its
// body. Module-level so the element refs stay stable — on reorder (the only
// time SidepanelSections re-renders) React's same-element bail-out skips
// re-rendering these heavy subtrees; the DOM nodes still move by key and framer
// animates the transform, but the trees don't re-run. Adding/removing a section
// is a single entry here (plus the key in atoms' DEFAULT order).
const SECTIONS: Record<SidepanelSectionKey, SectionDescriptor> = {
  upcoming: {
    icon: <Calendar />,
    label: 'Upcoming',
    name: 'Upcoming',
    body: <UpcomingSectionBody />,
    subtitle: <UpcomingLoadingIndicator />,
    viewAll: (openView) => openView('upcoming', undefined, { sourceLabel: 'Home' }),
  },
  actions: {
    icon: <UserStar />,
    label: 'For You',
    name: 'For You',
    body: <SidepanelRunningChatsSection />,
    badge: <ForYouUnreadBadge />,
    viewAll: (openView) => openView('forYou', undefined, { sourceLabel: 'Home' }),
  },
  reminders: {
    icon: <ListChecks />,
    label: 'Reminders',
    name: 'Reminders',
    body: <SidepanelRemindersSection />,
    badge: <RemindersBadge />,
    // Active is what the preview above is a slice of — the remembered tab could
    // be All or Resolved, neither of which is the list the user clicked.
    viewAll: (openView) => openView('reminders', { initialTab: 'active' }, { sourceLabel: 'Home' }),
  },
  history: {
    icon: <Clock />,
    label: 'Recents',
    name: 'Recents',
    body: <SidepanelHistorySection />,
    viewAll: (openView) => openView('history', undefined, { sourceLabel: 'Home' }),
  },
}

/**
 * Header + body wired to the shared base-ui collapsible: the header's chevron is
 * the `Collapsible.Trigger`, and the body is the panel it opens.
 *
 * Controlled (not `defaultOpen`) because the collapsed set is persisted in
 * GlobalStore, which can hydrate after this mounts — an uncontrolled panel would
 * latch the pre-hydration value and read as "nothing was remembered".
 *
 * `header` and `children` arrive as already-built elements, so this component
 * re-rendering on a toggle doesn't re-render either subtree.
 */
const SectionCollapsible = memo(function SectionCollapsible({
  sectionKey,
  header,
  children,
}: {
  sectionKey: SidepanelSectionKey
  header: ReactNode
  children: ReactNode
}) {
  const collapsed = useIsSidepanelSectionCollapsed(sectionKey)
  const setCollapsed = useSetSidepanelSectionCollapsed()

  // The panel clips so the fold can't spill onto the next section, but rows paint
  // outside themselves (hover hairline, shadow, focus ring) and the clip edge sits
  // flush against them. So the section's own 12px moves *inside* the clip box and
  // is cancelled with a negative margin: same layout, bleed room back. The 6px
  // header gap moves in the same way, onto the inner. Mid-fold the padding band
  // shows content 12px past the fold line — it lands in the section's own padding,
  // reading as the cut at the divider.
  return (
    <Collapsible open={!collapsed} onOpenChange={(open) => setCollapsed(sectionKey, !open)} className="w-full gap-0">
      {header}
      <Collapsible.Content
        className="after:from-background -mx-3 -mb-3 px-3 pb-3 after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-10 after:bg-gradient-to-t after:to-transparent after:opacity-0 after:transition-opacity after:duration-150 data-[ending-style]:after:opacity-100 motion-reduce:after:hidden"
        innerClassName="mt-0 cursor-auto gap-0 pt-1.5"
      >
        {children}
      </Collapsible.Content>
    </Collapsible>
  )
})

interface ReorderableSectionProps {
  value: SidepanelSectionKey
  dragEnabled: boolean
  sectionClassName: string
  dataTourSection?: string
  lead: ReactNode
  trailing: ReactNode
  onViewAll?: () => void
  shouldSuppressClick: () => boolean
  children: ReactNode
  onDragStart: (pointerY: number | undefined) => void
  onDragEnd: () => void
}

// framer's drag-start event is a pointer/mouse/touch event; pull the viewport
// `clientY` out so auto-scroll has the real starting position immediately.
const getEventClientY = (event: MouseEvent | TouchEvent | PointerEvent): number | undefined => {
  if ('clientY' in event) return event.clientY
  return event.changedTouches[0]?.clientY ?? event.touches[0]?.clientY
}

// Memoized so an unrelated SidepanelSections re-render (parent state changes,
// quick-tour toggles) doesn't re-run the framer Reorder.Item. On an actual
// reorder the items still update — Reorder.Item consumes the group context, so
// it re-renders on order changes regardless of this memo boundary.
const ReorderableSection = memo(function ReorderableSection({
  value,
  dragEnabled,
  sectionClassName,
  dataTourSection,
  lead,
  trailing,
  onViewAll,
  shouldSuppressClick,
  children,
  onDragStart,
  onDragEnd,
}: ReorderableSectionProps) {
  const controls = useDragControls()
  const [isDragging, setIsDragging] = useState(false)

  // `dragListener={false}` keeps the body and header inert; only the grip
  // explicitly starts a reorder.
  const handlePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (!dragEnabled || e.button !== 0) {
        return
      }

      controls.start(e)
    },
    [dragEnabled, controls],
  )

  const handleHeaderClick = useCallback(
    (event: ReactMouseEvent) => {
      if (shouldSuppressClick()) {
        return
      }
      // The left title trigger collapses, the right action navigates, and the
      // grip reorders. Only the unclaimed header backdrop delegates to View all.
      const clickedDragHandle =
        event.target instanceof Element && event.target.closest('[data-section-drag-handle]') !== null
      if (isDragExcluded(event.target) || clickedDragHandle) {
        return
      }
      onViewAll?.()
    },
    [onViewAll, shouldSuppressClick],
  )

  return (
    <Reorder.Item
      as="div"
      value={value}
      dragListener={false}
      dragControls={controls}
      dragTransition={dropDragTransition}
      layout="position"
      transition={reorderTransition}
      onDragStart={(event) => {
        setIsDragging(true)
        onDragStart(getEventClientY(event))
      }}
      onDragEnd={() => {
        setIsDragging(false)
        onDragEnd()
      }}
      style={{ position: 'relative' }}
      className={twMerge(isDragging && 'z-10')}
    >
      <SidepanelSection
        data-section={value}
        data-tour-section={dataTourSection}
        className={twMerge(
          sectionClassName,
          // Lift the dragged section out of the flat stack so it reads as a
          // slab while it travels over its siblings — square, so it stays the
          // full-bleed band it is at rest. The divider goes transparent (not
          // removed) so the box height doesn't shift mid-drag.
          isDragging && 'bg-background border-b-transparent shadow-lg',
        )}
      >
        <SectionCollapsible
          sectionKey={value}
          header={
            <SidepanelSectionHeader
              onClick={handleHeaderClick}
              className={twMerge(onViewAll && 'cursor-pointer', 'select-none')}
            >
              {lead}
              {dragEnabled && (
                <SidepanelSectionDragHandle
                  onPointerDown={handlePointerDown}
                  className="cursor-grab active:cursor-grabbing"
                />
              )}
              {trailing}
            </SidepanelSectionHeader>
          }
        >
          {children}
        </SectionCollapsible>
      </SidepanelSection>
    </Reorder.Item>
  )
})

interface SidepanelSectionsProps {
  getQuickTourSectionClass: (section: QuickTourTarget | 'history') => string
  dragEnabled: boolean
}

/** Switches the `home` surface: the reorderable sections, or Upcoming over one To-dos list. */
export function SidepanelSections(props: SidepanelSectionsProps) {
  return useSidepanelSurface('home') === 'todos' ? (
    <TodosHome getQuickTourSectionClass={props.getQuickTourSectionClass} />
  ) : (
    <ClassicSidepanelSections {...props} />
  )
}

/**
 * The reorderable body sections (upcoming, actions, reminders, history).
 *
 * Order is persisted via `useSidepanelSectionOrder`; sections are dragged by
 * their header. Drag is disabled during the quick tour so the tour's section
 * highlighting stays put. The tour itself locates sections by their
 * `data-tour-section` attribute, so a custom order never affects it.
 */
function ClassicSidepanelSections({ getQuickTourSectionClass, dragEnabled }: SidepanelSectionsProps) {
  const { openView } = useViewActions()
  const storedOrder = useSidepanelSectionOrder()
  const setOrder = useSetSidepanelSectionOrder()
  const {
    begin: beginSuppressingPostDragClicks,
    end: endSuppressingPostDragClicks,
    shouldSuppress: shouldSuppressPostDragClick,
  } = usePostDragClickGuard()

  const sanitizedOrder = useMemo(() => sanitizeSidepanelSectionOrder(storedOrder), [storedOrder])

  // Live order during a drag. framer's `onReorder` fires on every threshold
  // crossing, so writing straight to the persisted GlobalStore atom would
  // broadcast to every renderer (and hit disk) several times per drag. Instead
  // we reorder locally and persist once on drop. The order is mirrored in a ref
  // so the drag-end handler can read the latest without re-creating callbacks.
  const [order, setLiveOrder] = useState(sanitizedOrder)
  const orderRef = useRef(order)
  orderRef.current = order
  const isDraggingRef = useRef(false)
  const dragStartOrderRef = useRef(order)
  // Latest stored order, mirrored so drag-end can adopt a change the sync
  // effect skipped while the drag was in progress.
  const sanitizedOrderRef = useRef(sanitizedOrder)
  sanitizedOrderRef.current = sanitizedOrder

  // Anchors the auto-scroll hook's scrollable-ancestor lookup. Destructure the
  // stable start/stop callbacks so the drag handlers below keep stable identity
  // (the hook returns a fresh object each render) and the memoized items hold.
  const groupRef = useRef<HTMLDivElement>(null)
  const { start: startAutoScroll, stop: stopAutoScroll } = useDragAutoScroll()

  const startInstantLayout = useInstantLayoutTransition()

  // Adopt external/stored changes (initial hydration, another renderer) only
  // when not mid-drag, so a live drag isn't yanked out from under the user.
  // Instant, pre-paint (useLayoutEffect): framer clears the block on its next frame.
  useLayoutEffect(() => {
    if (isDraggingRef.current || sameOrder(orderRef.current, sanitizedOrder)) return
    startInstantLayout(() => setLiveOrder(sanitizedOrder))
  }, [sanitizedOrder, startInstantLayout])

  // A hidden section (empty Recents) is omitted from rendering but stays in
  // `values` — framer copies the full values array on reorder, so unrendered
  // keys keep their slot and hiding never mutates the persisted order.
  const handleReorder = useCallback((next: Array<SidepanelSectionKey>) => setLiveOrder(next), [])

  const handleDragStart = useCallback(
    (pointerY: number | undefined) => {
      beginSuppressingPostDragClicks()
      isDraggingRef.current = true
      dragStartOrderRef.current = orderRef.current
      startAutoScroll(groupRef.current, pointerY)
    },
    [beginSuppressingPostDragClicks, startAutoScroll],
  )

  const handleDragEnd = useCallback(() => {
    isDraggingRef.current = false
    stopAutoScroll()
    // Persist once per drag, and only if the order actually changed —
    // atomWithStorage writes (and broadcasts) unconditionally otherwise.
    const next = orderRef.current
    if (!sameOrder(dragStartOrderRef.current, next)) {
      setOrder(next)
    } else if (!sameOrder(sanitizedOrderRef.current, next)) {
      // No user change, but storage moved mid-drag (another window / late
      // hydration) and the sync effect skipped it. Adopt it now so the panel
      // doesn't stay stale until remount.
      startInstantLayout(() => setLiveOrder(sanitizedOrderRef.current))
    }
    endSuppressingPostDragClicks()
  }, [endSuppressingPostDragClicks, setOrder, stopAutoScroll, startInstantLayout])

  // Recents has no empty state — the whole section (header included) is absent
  // until the account has any history (Day 0 design). Hidden only once the
  // fetch has definitively answered `false`, so accounts WITH history don't get
  // a pop-in while it loads; the value flips at most once per presence change.
  const hasHistory = useHasHistoryActivity()
  const visibleOrder = useMemo(
    () => (hasHistory === false ? order.filter((key) => key !== 'history') : order),
    [hasHistory, order],
  )

  // Headers close over `openView` (stable identity), so memoizing keeps their
  // element refs stable across re-renders too — only the live badges/toggles
  // inside re-render via their own subscriptions.
  const headers = useMemo<Record<SidepanelSectionKey, SectionHeaderContent>>(() => {
    const build = (key: SidepanelSectionKey) => {
      const { icon, label, name, badge, subtitle, viewAll, headerAction } = SECTIONS[key]
      const onViewAll = viewAll
        ? () => {
            if (shouldSuppressPostDragClick()) {
              return
            }
            AmplitudeRenderer.trackEvent('Sidepanel Section', { action: 'view all', section: key })
            viewAll(openView)
          }
        : undefined
      return {
        onViewAll,
        lead: (
          <SidepanelSectionHeaderLead>
            <SidepanelSectionTitleGroup data-no-drag>
              <SidepanelSectionIcon>{icon}</SidepanelSectionIcon>
              <SidepanelSectionLabel>
                {label}
                {badge}
              </SidepanelSectionLabel>
            </SidepanelSectionTitleGroup>
          </SidepanelSectionHeaderLead>
        ),
        trailing: (
          <>
            {/* The subtitle and "View all" share the header's right edge, so the
                subtitle steps aside while the header is hovered rather than the
                two sitting side by side. */}
            {subtitle && (
              <SidepanelSectionSubtitle className="transition-opacity duration-150 group-focus-within/section-header:opacity-0 group-hover/section-header:opacity-0 motion-reduce:transition-none">
                {subtitle}
              </SidepanelSectionSubtitle>
            )}
            {headerAction ?? (
              <SidepanelSectionViewAll
                aria-label={`View all ${name}`}
                onClick={onViewAll}
                data-tour-section={`${key}-view-all`}
              />
            )}
          </>
        ),
      }
    }
    return {
      upcoming: build('upcoming'),
      actions: build('actions'),
      reminders: build('reminders'),
      history: build('history'),
    }
  }, [openView, shouldSuppressPostDragClick])

  return (
    <Reorder.Group
      ref={groupRef}
      as="div"
      axis="y"
      values={order}
      onReorder={handleReorder}
      // Last section drops its divider (per design); selector-based so it
      // tracks reorders without per-item state.
      className="flex flex-col [&>*:last-child_[data-section]]:border-b-0"
    >
      {visibleOrder.map((key) => (
        <ReorderableSection
          key={key}
          value={key}
          dragEnabled={dragEnabled}
          sectionClassName={getQuickTourSectionClass(key)}
          dataTourSection={key}
          lead={headers[key].lead}
          trailing={headers[key].trailing}
          onViewAll={headers[key].onViewAll}
          shouldSuppressClick={shouldSuppressPostDragClick}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {SECTIONS[key].body}
        </ReorderableSection>
      ))}
    </Reorder.Group>
  )
}
